import {
  Activity,
  Bot,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  Command,
  FileClock,
  Gauge,
  LogOut,
  Menu,
  RefreshCw,
  Search,
  Settings,
  Shield,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { api, ApiError } from "./api";
import { OperationsPage, OverviewPage, PaidUnlinkedPage, SettingsPage, MembersPage, ReviewPage } from "./pages";

interface Toast {
  message: string;
  tone: "success" | "error" | "info";
}

export interface PageContext {
  notify: (message: string, tone?: Toast["tone"]) => void;
}

const nav = [
  { to: "/", label: "Overview", icon: Gauge, end: true },
  { to: "/review", label: "Review queue", icon: ClipboardCheck },
  { to: "/members", label: "All members", icon: Users },
  { to: "/paid-unlinked", label: "Paid · unlinked", icon: CircleDollarSign },
  { to: "/operations", label: "Operations", icon: FileClock },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function App() {
  const [auth, setAuth] = useState<"loading" | "in" | "out">("loading");
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    void api.session().then(() => setAuth("in")).catch(() => setAuth("out"));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (auth === "loading") return <LoadingScreen />;
  if (auth === "out") return <Login onAuthenticated={() => setAuth("in")} />;
  const context: PageContext = {
    notify: (message, tone = "success") => setToast({ message, tone }),
  };

  return (
    <Shell onLogout={() => void api.logout().finally(() => setAuth("out"))}>
      <Routes>
        <Route path="/" element={<OverviewPage context={context} />} />
        <Route path="/review" element={<ReviewPage context={context} />} />
        <Route path="/members" element={<MembersPage context={context} />} />
        <Route path="/paid-unlinked" element={<PaidUnlinkedPage context={context} />} />
        <Route path="/operations" element={<OperationsPage context={context} />} />
        <Route path="/settings" element={<SettingsPage context={context} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {toast && <div className={`toast toast-${toast.tone}`}><span>{toast.message}</span><button onClick={() => setToast(null)}><X size={15} /></button></div>}
    </Shell>
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <BrandMark />
      <div className="loading-line"><span /></div>
    </div>
  );
}

function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(password);
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not sign in");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="login-page">
      <div className="login-orbit orbit-one" />
      <div className="login-orbit orbit-two" />
      <section className="login-card">
        <BrandMark />
        <div className="login-copy">
          <span className="eyebrow">Membership operations</span>
          <h1>Know exactly who belongs.</h1>
          <p>Reconcile Telegram activity with Whop access, review exceptions, and action decisions without losing the audit trail.</p>
        </div>
        <form onSubmit={submit}>
          <label className="field-label">Admin password<input type="password" autoFocus autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter your dashboard password" /></label>
          {error && <div className="login-error">{error}</div>}
          <button className="button primary login-button" disabled={busy || !password}>{busy ? "Checking…" : "Open dashboard"}<Command size={16} /></button>
        </form>
        <div className="login-security"><Shield size={15} /><span>Private dashboard · audited actions · fail-closed removals</span></div>
      </section>
      <aside className="login-aside">
        <div className="signal-card"><span className="signal-dot" /><div><strong>Live reconciliation</strong><small>Telegram + Whop + operator decisions</small></div></div>
        <div className="login-stat"><strong>3</strong><span>independent checks before any automated removal</span></div>
      </aside>
    </main>
  );
}

function BrandMark() {
  return (
    <div className="brand">
      <span className="brand-icon"><Bot size={20} /></span>
      <div><strong>Kairo</strong><span>Membership Guard</span></div>
    </div>
  );
}

function Shell({ children, onLogout }: { children: ReactNode; onLogout: () => void }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);
  const active = nav.find((item) => (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)));
  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-top"><BrandMark /><button className="icon-button mobile-close" onClick={() => setMobileOpen(false)}><X size={19} /></button></div>
        <nav>
          <span className="nav-label">Workspace</span>
          {nav.map((item) => {
            const Icon = item.icon;
            return <NavLink key={item.to} to={item.to} end={item.end}><Icon size={18} /><span>{item.label}</span></NavLink>;
          })}
        </nav>
        <div className="sidebar-foot">
          <div className="observation-note"><span><Activity size={15} /></span><div><strong>Privacy-aware</strong><small>Activity timestamps only. Message content is never stored.</small></div></div>
          <button className="logout-button" onClick={onLogout}><LogOut size={17} /> Sign out</button>
        </div>
      </aside>
      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />}
      <section className="main-panel">
        <header className="mobile-header"><button className="icon-button" onClick={() => setMobileOpen(true)}><Menu size={20} /></button><span>{active?.label}</span><BrandMark /></header>
        {children}
      </section>
    </div>
  );
}

export function PageHeader({ eyebrow, title, copy, actions }: { eyebrow?: string; title: string; copy: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h1>{title}</h1><p>{copy}</p></div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function RefreshButton({ onClick, busy }: { onClick: () => void; busy?: boolean }) {
  return <button className="button secondary" onClick={onClick} disabled={busy}><RefreshCw size={16} className={busy ? "spin" : ""} /> Refresh</button>;
}

export function SearchField({ value, onChange, placeholder = "Search members…" }: { value: string; onChange: (value: string) => void; placeholder?: string }) {
  return <label className="search-field"><Search size={16} /><input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} /></label>;
}

export function SelectField({ value, onChange, children, ariaLabel }: { value: string; onChange: (value: string) => void; children: ReactNode; ariaLabel: string }) {
  return <label className="select-field"><select aria-label={ariaLabel} value={value} onChange={(event) => onChange(event.target.value)}>{children}</select><ChevronDown size={14} /></label>;
}
